import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";
import { useTourMode } from "../../../../contexts/TourModeContext";
import { StockAnalysisReportData } from "../../../../types/StockAnalysisReport";

export interface ReportListItem {
  id: string;
  ticker: string;
  quarter: string;
  year: number;
  generatedAt: string;
}

export interface ReportsListResponse {
  reports: ReportListItem[];
  ticker: string;
  totalCount: number;
}

/**
 * Hook to fetch all stock analysis reports for a specific ticker
 * Returns reports ordered by generation date (newest first)
 */
export const useReportsList = (ticker: string) => {
  const { isTourMode, getMockData } = useTourMode();

  return useQuery<ReportsListResponse, Error>({
    queryKey: ["stockAnalysisReportsList", ticker, isTourMode],
    queryFn: async () => {
      // In tour mode, return a list with the mock report
      if (isTourMode) {
        const mockReport = getMockData<StockAnalysisReportData>("aiAnalysisReport");
        if (mockReport) {
          return {
            reports: [
              {
                id: mockReport.id,
                ticker: mockReport.ticker,
                quarter: mockReport.quarter,
                year: mockReport.year,
                generatedAt: mockReport.generatedAt,
              },
            ],
            ticker: mockReport.ticker,
            totalCount: 1,
          };
        }
        return { reports: [], ticker, totalCount: 0 };
      }

      const response = await api.get<ReportsListResponse>(
        "/stock-analysis-reports/list",
        {
          params: { ticker },
        }
      );
      return response.data;
    },
    enabled: isTourMode || !!ticker,
    staleTime: isTourMode ? Infinity : 2 * 60 * 1000, // Never stale in tour mode
    gcTime: isTourMode ? Infinity : 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};
